import { useState, useRef, useEffect } from "react";
import {
  Mic,
  Video,
  RotateCcw,
  Upload,
  X,
  Square,
  Pause,
  Play,
  ListChecks,
  ChevronDown,
  SwitchCamera,
  Loader2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { RecordingData } from "../../hooks/useMediaRecorder";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import {
  getPresignedUploadUrl,
  uploadRecordingToR2,
  saveRecordingMetadata,
} from "../../api/recordings";
import {
  getPresignedUploadUrlForPrescription,
  uploadRecordingToR2 as uploadRecordingToR2Prescription,
  saveRecordingMetadataForPrescription,
} from "../../api/prescriptionRecordings";
import useRetryFetch from "../../hooks/useRetryFetch";
import { MinimalEditor } from "../reactflow/infoPopover/MinimalEditor";
import fixWebmDuration from "fix-webm-duration";

type RecordingType = "audio" | "video";
type DialogState =
  | "intro"
  | "camera_ready"
  | "audio_ready"
  | "recording"
  | "preview"
  | "playback";
type FacingMode = "user" | "environment";
type DurationZone = "green" | "orange" | "red" | "exceeded";

// Duration thresholds for traffic light system
const DURATION_THRESHOLDS = {
  MAX_DURATION: 60,
  GREEN_ZONE: { start: 0, end: 25 },
  ORANGE_ZONE: { start: 26, end: 45 },
  RED_ZONE: { start: 46, end: 60 },
};

// Determine which zone the recording is in
const getDurationZone = (seconds: number): DurationZone => {
  if (seconds <= 25) return "green";
  if (seconds <= 45) return "orange";
  if (seconds < 60) return "red";
  return "exceeded";
};

// Get styling and messaging for each zone (t passed from component for i18n)
const getZoneContent = (
  zone: DurationZone,
  t: (key: string, fallback?: string) => string
) => {
  switch (zone) {
    case "green":
      return {
        bgColor: "bg-green-50 dark:bg-green-950",
        borderColor: "border-green-500",
        dotColor: "bg-green-600",
        textColor: "text-green-600",
        message: t("duration_zone_green", "Perfect! Brief and to the point"),
      };
    case "orange":
      return {
        bgColor: "bg-orange-50 dark:bg-orange-950",
        borderColor: "border-orange-500",
        dotColor: "bg-orange-600",
        textColor: "text-orange-600",
        message: t(
          "duration_zone_orange",
          "Still good, but consider wrapping up"
        ),
      };
    case "red":
      return {
        bgColor: "bg-red-50 dark:bg-red-950",
        borderColor: "border-red-500",
        dotColor: "bg-red-600",
        textColor: "text-red-600",
        message: t(
          "duration_zone_red",
          "Almost at limit - finish your thought"
        ),
      };
    default:
      return null;
  }
};

interface RecordingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: RecordingType;
  onComplete: (data: RecordingData) => void;
  existingRecording?: RecordingData; // For playback mode
  mode?: "record" | "playback"; // Determine initial state
  context?: "pane" | "prologue" | "epilogue" | "additionalInfo"; // Context for customized text
  processId?: string; // Process ID for R2 upload
  activityId?: string; // Activity ID for pane context
}

// File size limits (in bytes)
const MAX_SIZES = {
  audio: 5 * 1024 * 1024, // 5 MB
  video: 30 * 1024 * 1024, // 30 MB
};

export const RecordingDialog = ({
  open,
  onOpenChange,
  type,
  onComplete,
  existingRecording,
  mode = "record",
  context = "pane",
  processId,
  activityId,
}: RecordingDialogProps) => {
  const { t } = useTranslation();
  const [dialogState, setDialogState] = useState<DialogState>("intro");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedData, setRecordedData] = useState<RecordingData | null>(null);
  const [actualDuration, setActualDuration] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Video: show live preview before recording; allow camera switch
  const [facingMode, setFacingMode] = useState<FacingMode>("user");
  const [cameraReadyLoading, setCameraReadyLoading] = useState(false);

  // Ephemeral pre-recording notes (not persisted)
  const [talkingPoints, setTalkingPoints] = useState<string>("");
  const [showNotesPanel, setShowNotesPanel] = useState(false);

  const retryFetch = useRetryFetch();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0); // Track total paused time
  const pauseStartRef = useRef<number>(0); // Track when pause started
  const isPausedRef = useRef<boolean>(false); // Ref for interval callback to stop counting when paused
  const autoStoppedRef = useRef<boolean>(false); // Track if auto-stopped to prevent duplicate calls
  const livePreviewRef = useRef<HTMLVideoElement>(null);
  const previewAudioRef = useRef<HTMLAudioElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const playbackMediaRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
  const fixedBlobUrlRef = useRef<string | null>(null);
  const hasStartedPlaybackRef = useRef(false);
  const teleprompterRef = useRef<HTMLDivElement>(null);

  // Playback: src to use (may be fixed blob URL for remote WebM without duration)
  const [playbackSrc, setPlaybackSrc] = useState<string | null>(null);
  const [playbackLoading, setPlaybackLoading] = useState(false);
  const [playbackMetadataReady, setPlaybackMetadataReady] = useState(false);

  // Set initial state based on mode
  useEffect(() => {
    if (open) {
      if (mode === "playback" && existingRecording) {
        setDialogState("playback");
        setRecordedData(existingRecording);
        setPlaybackMetadataReady(false);
      } else {
        setDialogState("intro");
        setRecordedData(null);
        stopStream();
        setPlaybackSrc(null);
        setPlaybackLoading(false);
      }
      setError(null);
    }
  }, [open, mode, existingRecording]);

  // Prepare playback src: for remote WebM without duration metadata, fetch + fix
  useEffect(() => {
    if (dialogState !== "playback" || !recordedData) return;

    hasStartedPlaybackRef.current = false;

    const url = recordedData.url || recordedData.blobUrl;
    const mimeType = recordedData.mimeType || "";
    const durationSecs = recordedData.duration;

    // Blob URLs are already fixed (new recordings); use directly
    if (recordedData.blobUrl && !recordedData.url) {
      setPlaybackSrc(recordedData.blobUrl);
      setPlaybackLoading(false);
      return;
    }

    // Remote URL: for WebM with known duration, fetch and fix to avoid progress bar bug
    const isRemoteWebm =
      recordedData.url &&
      mimeType.toLowerCase().includes("webm") &&
      typeof durationSecs === "number" &&
      durationSecs > 0;

    if (!isRemoteWebm || !url) {
      setPlaybackSrc(url || null);
      setPlaybackLoading(false);
      return;
    }

    let cancelled = false;
    setPlaybackLoading(true);
    setPlaybackSrc(null);

    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;

        const fixedBlob = await fixWebmDuration(blob, durationSecs * 1000, {
          logger: false,
        });
        if (cancelled) return;

        const blobUrl = URL.createObjectURL(fixedBlob);
        fixedBlobUrlRef.current = blobUrl;
        setPlaybackSrc(blobUrl);
      } catch (err) {
        if (!cancelled) {
          console.warn("fix-webm-duration failed, using original URL:", err);
          setPlaybackSrc(url);
        }
      } finally {
        if (!cancelled) setPlaybackLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (fixedBlobUrlRef.current) {
        URL.revokeObjectURL(fixedBlobUrlRef.current);
        fixedBlobUrlRef.current = null;
      }
    };
  }, [dialogState, recordedData]);

  // Reset UI state (but not talking points) when dialog closes
  useEffect(() => {
    if (!open) {
      setShowNotesPanel(false);
    }
  }, [open]);

  // Attach stream to video in camera_ready (see yourself before starting)
  useEffect(() => {
    if (
      dialogState === "camera_ready" &&
      type === "video" &&
      streamRef.current &&
      livePreviewRef.current
    ) {
      livePreviewRef.current.srcObject = streamRef.current;
    }
  }, [dialogState, type]);

  // Setup live preview for video recording (when transitioning to recording state)
  useEffect(() => {
    if (
      isRecording &&
      type === "video" &&
      streamRef.current &&
      livePreviewRef.current
    ) {
      livePreviewRef.current.srcObject = streamRef.current;
    }
  }, [isRecording, type]);

  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      stopStream();
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (fixedBlobUrlRef.current) {
        URL.revokeObjectURL(fixedBlobUrlRef.current);
        fixedBlobUrlRef.current = null;
      }
    };
  }, []);

  // Revoke fixed blob URL when dialog closes
  useEffect(() => {
    if (!open && fixedBlobUrlRef.current) {
      URL.revokeObjectURL(fixedBlobUrlRef.current);
      fixedBlobUrlRef.current = null;
    }
  }, [open]);

  // Integer seconds only (floor), to match native player which shows whole seconds (e.g. 3.59 → 0:03)
  const formatTime = (seconds: number): string => {
    const totalSecs = Math.floor(Number(seconds));
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getMimeType = (): string => {
    if (type === "audio") {
      const audioTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      for (const mimeType of audioTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          return mimeType;
        }
      }
      return "audio/webm";
    } else {
      const videoTypes = [
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp9,opus",
        "video/webm",
        "video/mp4",
      ];
      for (const mimeType of videoTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          return mimeType;
        }
      }
      return "video/webm";
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (livePreviewRef.current) {
      livePreviewRef.current.srcObject = null;
    }
  };

  const getVideoConstraints = (): MediaStreamConstraints => ({
    video: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      facingMode,
    },
    audio: true,
  });

  const openCameraForVideo = async () => {
    setCameraReadyLoading(true);
    setError(null);
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        getVideoConstraints()
      );
      streamRef.current = stream;
      setDialogState("camera_ready");
    } catch (err) {
      const error = err as Error;
      const errorMessage =
        error.name === "NotAllowedError"
          ? t(
              "permission_denied_camera_mic",
              "Permission denied. Please allow access to your camera and microphone."
            )
          : error.name === "NotFoundError"
          ? t("no_camera_found", "No camera found.")
          : error.message ||
            t("failed_to_start_recording", "Failed to start recording");
      setError(errorMessage);
      console.error("Camera error:", err);
    } finally {
      setCameraReadyLoading(false);
    }
  };

  const switchCamera = async () => {
    const nextMode: FacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(nextMode);
    setError(null);
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: nextMode,
        },
        audio: true,
      });
      streamRef.current = stream;
      if (livePreviewRef.current) {
        livePreviewRef.current.srcObject = stream;
      }
    } catch (err) {
      const error = err as Error;
      setError(
        error.message ||
          t("failed_to_start_recording", "Failed to start recording")
      );
      setFacingMode(facingMode); // Revert on failure
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      chunksRef.current = [];

      let stream: MediaStream;
      if (type === "audio") {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } else {
        if (streamRef.current) {
          stream = streamRef.current;
        } else {
          stream = await navigator.mediaDevices.getUserMedia(
            getVideoConstraints()
          );
        }
      }
      streamRef.current = stream;

      const mimeType = getMimeType();
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const elapsedMs =
          Date.now() - startTimeRef.current - pausedTimeRef.current;
        const durationSecs = Math.floor(elapsedMs / 1000);
        const blob = new Blob(chunksRef.current, { type: mimeType });

        const applyBlob = (finalBlob: Blob) => {
          const blobUrl = URL.createObjectURL(finalBlob);
          const data: RecordingData = {
            blobUrl,
            mimeType,
            size: finalBlob.size,
            duration: durationSecs,
            recordedAt: new Date().toISOString(),
          };
          setRecordedData(data);
          setActualDuration(null);
          setDialogState("preview");
          stopStream();
        };

        if (mimeType.includes("webm")) {
          fixWebmDuration(blob, elapsedMs, { logger: false })
            .then(applyBlob)
            .catch((err) => {
              console.warn("fix-webm-duration failed, using raw blob:", err);
              applyBlob(blob);
            });
        } else {
          applyBlob(blob);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setError(t("recording_error", "Recording error occurred"));
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setIsPaused(false);
      isPausedRef.current = false;
      setRecordingTime(0);
      startTimeRef.current = Date.now();
      pausedTimeRef.current = 0;
      autoStoppedRef.current = false; // Reset auto-stop flag
      setDialogState("recording");

      timerIntervalRef.current = setInterval(() => {
        if (isPausedRef.current) return;
        const elapsed = Math.floor(
          (Date.now() - startTimeRef.current - pausedTimeRef.current) / 1000
        );
        setRecordingTime(elapsed);

        // Auto-stop at 60 seconds
        if (
          elapsed >= DURATION_THRESHOLDS.MAX_DURATION &&
          !autoStoppedRef.current
        ) {
          autoStoppedRef.current = true; // Prevent duplicate calls
          // Clear interval immediately to prevent multiple calls
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          toast.info(
            t(
              "recording_complete_limit",
              "Recording complete - 1 minute limit reached"
            )
          );
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      const error = err as Error;
      const errorMessage =
        error.name === "NotAllowedError"
          ? type === "audio"
            ? t(
                "permission_denied_mic",
                "Permission denied. Please allow access to your microphone."
              )
            : t(
                "permission_denied_camera_mic",
                "Permission denied. Please allow access to your camera and microphone."
              )
          : error.name === "NotFoundError"
          ? type === "audio"
            ? t("no_microphone_found", "No microphone found.")
            : t("no_camera_found", "No camera found.")
          : error.message ||
            t("failed_to_start_recording", "Failed to start recording");

      setError(errorMessage);
      console.error("Recording error:", error);
    }
  };

  const stopRecording = () => {
    // Check the actual mediaRecorder state, not the React state (which might be stale in closures)
    if (
      mediaRecorderRef.current &&
      (mediaRecorderRef.current.state === "recording" ||
        mediaRecorderRef.current.state === "paused")
    ) {
      // If paused, add current pause duration to pausedTimeRef so onstop computes correct duration
      if (mediaRecorderRef.current.state === "paused") {
        pausedTimeRef.current += Date.now() - pauseStartRef.current;
        mediaRecorderRef.current.resume();
      }

      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      isPausedRef.current = true;
      pauseStartRef.current = Date.now();

      // Stop the timer first so counter stops immediately
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      mediaRecorderRef.current.pause();
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      isPausedRef.current = false;

      // Add the paused duration to total paused time
      pausedTimeRef.current += Date.now() - pauseStartRef.current;

      mediaRecorderRef.current.resume();
      setIsPaused(false);

      // Restart the timer
      timerIntervalRef.current = setInterval(() => {
        if (isPausedRef.current) return;
        const elapsed = Math.floor(
          (Date.now() - startTimeRef.current - pausedTimeRef.current) / 1000
        );
        setRecordingTime(elapsed);

        // Auto-stop at 60 seconds
        if (
          elapsed >= DURATION_THRESHOLDS.MAX_DURATION &&
          !autoStoppedRef.current
        ) {
          autoStoppedRef.current = true; // Prevent duplicate calls
          // Clear interval immediately to prevent multiple calls
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          toast.info(
            t(
              "recording_complete_limit",
              "Recording complete - 1 minute limit reached"
            )
          );
          stopRecording();
        }
      }, 1000);
    }
  };

  // Keyboard shortcuts during recording: ↑/↓ scrolls teleprompter, Space/Escape stops
  // Must be declared after stopRecording to avoid TS2448
  useEffect(() => {
    if (dialogState !== "recording") return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        teleprompterRef.current?.scrollBy({ top: 60, behavior: "smooth" });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        teleprompterRef.current?.scrollBy({ top: -60, behavior: "smooth" });
      } else if (e.key === " " || e.key === "Escape") {
        e.preventDefault();
        stopRecording();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [dialogState]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetake = () => {
    if (recordedData?.blobUrl) {
      URL.revokeObjectURL(recordedData.blobUrl);
    }
    setRecordedData(null);
    setActualDuration(null);
    setDialogState("intro");
    setRecordingTime(0);
  };

  // Use media.duration as single source of truth; floor to match native player (e.g. 3.59 → 3)
  const handleMetadataLoaded = (e: React.SyntheticEvent<HTMLMediaElement>) => {
    const media = e.currentTarget;
    if (media.duration && isFinite(media.duration)) {
      const durationSecs = Math.floor(media.duration);
      setActualDuration(durationSecs);
      setRecordedData((prev) =>
        prev ? { ...prev, duration: durationSecs } : prev
      );
    }
  };

  const validateBeforeUpload = (): boolean => {
    if (!recordedData) return false;

    const maxSize = MAX_SIZES[type];
    if (recordedData.size > maxSize) {
      toast.error(
        t("file_size_exceeds_limit", "File size exceeds {{size}} MB limit", {
          size: (maxSize / 1048576).toFixed(0),
        })
      );
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!recordedData) return;

    // Validate file size on frontend
    if (!validateBeforeUpload()) {
      return;
    }

    // If no processId, store locally (deferred upload pattern)
    // The recording will be uploaded later when the parent saves
    if (!processId) {
      onComplete(recordedData);
      setTalkingPoints(""); // clear notes on successful completion
      // Don't close dialog here - let onComplete handler decide
      // For deferred upload (additionalInfo), the parent will close after saving
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Handle prescription recordings (additionalInfo context) differently
      if (context === "additionalInfo") {
        // Use prescription-specific APIs
        const {
          uploadUrl,
          key,
          maxSize: serverMaxSize,
        } = await getPresignedUploadUrlForPrescription(
          processId, // This is actually prescriptionId for additionalInfo context
          type,
          activityId || "",
          retryFetch
        );

        // Double-check size against server limit
        if (recordedData.size > serverMaxSize) {
          throw new Error(
            `File size exceeds server limit of ${(
              serverMaxSize / 1048576
            ).toFixed(1)} MB`
          );
        }

        // Upload blob directly to R2
        if (!recordedData.blobUrl) {
          throw new Error("Recording blob URL is missing");
        }
        const blob = await fetch(recordedData.blobUrl).then((r) => r.blob());
        await uploadRecordingToR2Prescription(
          uploadUrl,
          blob,
          recordedData.mimeType
        );

        // Save metadata to backend
        const { recording } = await saveRecordingMetadataForPrescription(
          processId,
          {
            key,
            type,
            activityId: activityId || "",
            duration: actualDuration || recordedData.duration,
            size: recordedData.size,
            mimeType: recordedData.mimeType,
          },
          retryFetch
        );

        // Call onComplete with updated data including R2 URL
        onComplete({
          ...recordedData,
          url: recording.url,
          key: recording.key,
        });
      } else {
        // Use process-specific APIs for pane, prologue, epilogue
        const {
          uploadUrl,
          key,
          maxSize: serverMaxSize,
        } = await getPresignedUploadUrl(
          processId,
          type,
          context as "pane" | "prologue" | "epilogue",
          context === "pane" ? activityId : undefined,
          retryFetch
        );

        // Double-check size against server limit
        if (recordedData.size > serverMaxSize) {
          throw new Error(
            `File size exceeds server limit of ${(
              serverMaxSize / 1048576
            ).toFixed(1)} MB`
          );
        }

        // Upload blob directly to R2
        if (!recordedData.blobUrl) {
          throw new Error("Recording blob URL is missing");
        }
        const blob = await fetch(recordedData.blobUrl).then((r) => r.blob());
        await uploadRecordingToR2(uploadUrl, blob, recordedData.mimeType);

        // Save metadata to backend
        const { recording } = await saveRecordingMetadata(
          processId,
          {
            key,
            type,
            context: context as "pane" | "prologue" | "epilogue",
            activityId: context === "pane" ? activityId : undefined,
            duration: actualDuration || recordedData.duration,
            size: recordedData.size,
            mimeType: recordedData.mimeType,
          },
          retryFetch
        );

        // Call onComplete with updated data including R2 URL
        onComplete({
          ...recordedData,
          url: recording.url,
          key: recording.key,
        });
      }

      toast.success(t("recording_uploaded", "Recording uploaded successfully"));
      setTalkingPoints(""); // clear notes on successful upload
      onOpenChange(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("failed_to_upload_recording", "Failed to upload recording");
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (isRecording) {
      stopRecording();
      stopStream();
    }
    if (dialogState === "camera_ready") {
      stopStream();
    }
    if (recordedData?.blobUrl && mode !== "playback") {
      URL.revokeObjectURL(recordedData.blobUrl);
    }
    if (fixedBlobUrlRef.current) {
      URL.revokeObjectURL(fixedBlobUrlRef.current);
      fixedBlobUrlRef.current = null;
    }
    onOpenChange(false);
  };

  // Play only when duration is valid and defer by one frame so slider paints correctly
  const tryStartPlayback = () => {
    const el = playbackMediaRef.current;
    if (!el || hasStartedPlaybackRef.current) return;
    const d = el.duration;
    if (!Number.isFinite(d) || d <= 0) return;
    hasStartedPlaybackRef.current = true;
    setPlaybackMetadataReady(true);
    requestAnimationFrame(() => {
      el.play().catch(() => {
        // Autoplay may be blocked; user can click play manually
      });
    });
  };

  const getIntroContent = () => {
    if (type === "audio") {
      const contextText =
        context === "prologue"
          ? t(
              "audio_note_context_prologue",
              "Use audio notes to explain the situation that led to this engagement, the problem you're addressing, or your initial approach."
            )
          : context === "epilogue"
          ? t(
              "audio_note_context_epilogue",
              "Use audio notes to reflect on what you learned, key insights you gained, or what you would do differently next time."
            )
          : context === "additionalInfo"
          ? t(
              "audio_note_context_additional_info",
              "Use audio notes to provide additional context, explain complex concepts, or give instructions that complement this activity step."
            )
          : t(
              "audio_note_context_pane",
              "Use audio notes to explain your thought process, trade-offs, or context behind this pane."
            );

      return {
        title: t("add_audio_note", "Add Audio Note"),
        description: [
          t(
            "audio_note_description",
            "Audio notes add a human layer to your work. Some ideas are clearer when spoken. Your tone and pauses help others understand your intent and reasoning."
          ),
          contextText,
        ],
        benefitsTitle: t("why_add_audio_note", "Why add an audio note?"),
        benefits: [
          t("audio_benefit_express", "Express ideas that are hard to write"),
          t("audio_benefit_context", "Add personal context and emotion"),
          t("audio_benefit_trust", "Build trust through your authentic voice"),
          t("audio_benefit_clarify", "Clarify complex thoughts quickly"),
        ],
        note: t(
          "audio_note_tip",
          "This doesn't need to be perfect—clarity matters more than polish. Short notes work best (20–25s). Maximum duration: 1 minute."
        ),
      };
    } else {
      const contextText =
        context === "prologue"
          ? t(
              "video_note_context_prologue",
              "Use video notes to explain the context and goals of this engagement, or visually describe the challenge you're tackling."
            )
          : context === "epilogue"
          ? t(
              "video_note_context_epilogue",
              "Use video notes to share your reflections, demonstrate key outcomes, or explain lessons learned from this engagement."
            )
          : context === "additionalInfo"
          ? t(
              "video_note_context_additional_info",
              "Use video notes to demonstrate concepts, show examples, or provide visual guidance that enhances this activity step."
            )
          : t(
              "video_note_context_pane",
              "Use video notes to explain why you made certain decisions in this pane or to visually demonstrate something that's hard to describe in text."
            );

      return {
        title: t("add_video_note", "Add Video Note"),
        description: [
          t(
            "video_note_description",
            "Video notes bring your work to life. Facial expressions, gestures, and visual demonstrations help others better understand your thinking and experience."
          ),
          contextText,
        ],
        benefitsTitle: t("why_add_video_note", "Why add a video note?"),
        benefits: [
          t("video_benefit_show", "Show instead of just tell"),
          t("video_benefit_connection", "Build stronger human connection"),
          t("video_benefit_demonstrate", "Demonstrate processes visually"),
          t("video_benefit_memorable", "Make your message more memorable"),
        ],
        note: t(
          "video_note_tip",
          "This doesn't need to be perfect—natural and honest is better than polished. Short notes work best (20–25s). Maximum duration: 1 minute."
        ),
      };
    }
  };

  const intro = getIntroContent();

  // Use wider dialog during recording, camera_ready (video), or audio_ready when notes exist
  const dialogMaxWidth =
    talkingPoints &&
    (dialogState === "recording" ||
      (dialogState === "camera_ready" && type === "video") ||
      (dialogState === "audio_ready" && type === "audio"))
      ? "max-w-5xl"
      : "max-w-3xl";

  // Handle dialog close - prevent accidental closure during recording or uploading
  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing while recording or uploading
    if (!newOpen && (isRecording || uploading)) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          dialogMaxWidth,
          "max-h-[90vh] overflow-y-auto",
          // Hide default close button for video playback (we show overlay button instead)
          dialogState === "playback" && type === "video" && "[&>button]:hidden",
          // Minimal padding for playback state
          dialogState === "playback" && "p-px"
        )}
        onPointerDownOutside={(e) => {
          // Prevent closing by clicking outside during camera_ready/audio_ready/recording/preview/upload
          if (
            dialogState === "camera_ready" ||
            dialogState === "audio_ready" ||
            isRecording ||
            uploading ||
            dialogState === "preview"
          ) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (
            dialogState === "camera_ready" ||
            dialogState === "audio_ready" ||
            isRecording ||
            uploading ||
            dialogState === "preview"
          ) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing via escape during recording/uploading
          if (isRecording || uploading) {
            e.preventDefault();
          }
        }}
      >
        {/* INTRO STATE */}
        {dialogState === "intro" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl">
                {type === "audio" ? (
                  <Mic className="w-6 h-6" />
                ) : (
                  <Video className="w-6 h-6" />
                )}
                {intro.title}
              </DialogTitle>
              <DialogDescription className="mt-4 space-y-3 text-base">
                {intro.description.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </DialogDescription>
            </DialogHeader>

            <div className="my-4 space-y-4">
              <div className="p-4 rounded-lg bg-muted/30">
                <h4 className="mb-3 text-sm font-semibold">
                  {intro.benefitsTitle}
                </h4>
                <ul className="space-y-2">
                  {intro.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Alert className="border-primary/20 bg-primary/5 dark:bg-indigo-950 dark:border-primary/80">
                <AlertDescription className="text-sm text-foreground">
                  <p className="mb-2 italic">{intro.note}</p>
                  <p>
                    {t(
                      "recording_upload_note",
                      "Note: Your recording will be uploaded only after you click Upload. It is not uploaded automatically after recording."
                    )}
                  </p>
                </AlertDescription>
              </Alert>

              {/* Collapsible Pre-Recording Notes Section */}
              <Collapsible
                open={showNotesPanel}
                onOpenChange={setShowNotesPanel}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="justify-between w-full hover:bg-muted/50"
                  >
                    <span className="flex items-center gap-2">
                      <ListChecks className="w-4 h-4" />
                      {t(
                        "prepare_talking_points",
                        "Prepare Talking Points (Optional)"
                      )}
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        showNotesPanel && "rotate-180"
                      )}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  <div className="p-3 border rounded-lg bg-muted/20">
                    <p className="mb-3 text-sm text-muted-foreground">
                      {t(
                        "talking_points_hint",
                        "Jot down key points to help you stay focused during recording. These notes will be visible while you record."
                      )}
                    </p>
                    <MinimalEditor
                      value={talkingPoints}
                      onChange={setTalkingPoints}
                      placeholder={t(
                        "talking_points_placeholder",
                        "• Main point 1\n• Main point 2\n• Key takeaway..."
                      )}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancel}>
                {t("cancel", "Cancel")}
              </Button>
              {type === "video" ? (
                <Button
                  onClick={openCameraForVideo}
                  disabled={cameraReadyLoading}
                  className="gap-2"
                >
                  {cameraReadyLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("opening_camera", "Opening camera...")}
                    </>
                  ) : (
                    <>
                      <Video className="w-4 h-4" />
                      {t("open_camera", "Open camera")}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => setDialogState("audio_ready")}
                  className="gap-2"
                >
                  <Mic className="w-4 h-4" />
                  {t("start_recording", "Start Recording")}
                </Button>
              )}
            </DialogFooter>
          </>
        )}

        {/* AUDIO_READY STATE (audio only): review talking points before Start Recording */}
        {dialogState === "audio_ready" && type === "audio" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mic className="w-6 h-6" />
                {t("audio_ready_title", "Ready to record")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "audio_ready_description",
                  "Review your talking points below if you added any, then start when ready."
                )}
              </DialogDescription>
            </DialogHeader>
            <div
              className={cn(
                "py-4",
                talkingPoints ? "flex gap-6" : "space-y-4"
              )}
            >
              <div
                className={cn(
                  "space-y-4",
                  talkingPoints ? "flex-1 min-w-0" : "w-full"
                )}
              >
                <div className="flex flex-col items-center justify-center py-8 rounded-lg bg-muted/20">
                  <Mic className="w-16 h-16 text-muted-foreground" />
                  <p className="mt-3 text-sm text-muted-foreground">
                    {t("audio_ready_hint", "Your microphone will start when you click below.")}
                  </p>
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </div>
              {talkingPoints && (
                <div className="flex-shrink-0 w-72">
                  <div className="flex flex-col h-full border rounded-lg bg-muted/20">
                    <div className="flex items-center gap-2 p-3 border-b rounded-t-lg bg-muted/30">
                      <ListChecks className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {t("your_talking_points", "Your Talking Points")}
                      </span>
                    </div>
                    <div
                      className="flex-1 p-3 overflow-y-auto text-sm prose-sm prose dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: talkingPoints }}
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancel}>
                {t("cancel", "Cancel")}
              </Button>
              <Button onClick={startRecording} className="gap-2">
                <Mic className="w-4 h-4" />
                {t("start_recording", "Start Recording")}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* CAMERA_READY STATE (video only): same UI as recording so user is familiar */}
        {dialogState === "camera_ready" && type === "video" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="w-6 h-6" />
                {t("camera_ready_title", "Position your camera")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "camera_ready_description",
                  "You'll see the same frame when recording. Switch camera if needed, then start when ready."
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden border-4 border-muted">
                <video
                  ref={livePreviewRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-contain"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={switchCamera}
                  className="absolute top-2 right-2 gap-2 z-10"
                  title={t("switch_camera", "Switch camera")}
                >
                  <SwitchCamera className="w-4 h-4" />
                  {t("switch_camera", "Switch camera")}
                </Button>
                {talkingPoints && (
                  <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-black/80 via-black/50 to-transparent pointer-events-none">
                    <div className="flex-1 p-4 overflow-y-auto">
                      <div
                        className="text-xl font-medium leading-relaxed text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] prose prose-invert prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 max-w-none prose-headings:text-2xl [&_*]:!text-white [&_a]:!text-white"
                        dangerouslySetInnerHTML={{ __html: talkingPoints }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancel}>
                {t("cancel", "Cancel")}
              </Button>
              <Button onClick={startRecording} className="gap-2">
                <Video className="w-4 h-4" />
                {t("start_recording", "Start Recording")}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* RECORDING STATE */}
        {dialogState === "recording" &&
          (() => {
            const currentZone = getDurationZone(recordingTime);
            const zoneContent = getZoneContent(currentZone, t);

            return (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {type === "audio"
                      ? t("recording_audio", "Recording Audio...")
                      : t("recording_video", "Recording Video...")}
                  </DialogTitle>
                </DialogHeader>

                {/* Side-by-side layout for audio when notes exist; video uses overlay on feed */}
                <div
                  className={cn(
                    "py-4",
                    talkingPoints && type === "audio" ? "flex gap-6" : ""
                  )}
                >
                  {/* Left side: Recording UI */}
                  <div
                    className={cn(
                      "space-y-3",
                      talkingPoints && type === "audio"
                        ? "flex-1 min-w-0"
                        : "w-full"
                    )}
                  >
                    {type === "video" && (
                      <div
                        className={cn(
                          "relative w-full aspect-video bg-black rounded-lg overflow-hidden border-4 transition-colors duration-500",
                          zoneContent?.borderColor
                        )}
                      >
                        <video
                          ref={livePreviewRef}
                          autoPlay
                          muted
                          playsInline
                          className="object-contain w-full h-full"
                        />

                        {/* Teleprompter overlay — scrollable via ↑/↓ keys */}
                        {talkingPoints && (
                          <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-black/80 via-black/50 to-transparent">
                            <div
                              ref={teleprompterRef}
                              className="flex-1 p-4 overflow-y-auto scroll-smooth"
                            >
                              <div
                                className="text-xl font-medium leading-relaxed text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] prose prose-invert prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 max-w-none prose-headings:text-2xl [&_*]:!text-white [&_a]:!text-white"
                                dangerouslySetInnerHTML={{ __html: talkingPoints }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Timer + controls pinned to bottom of video frame */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-4 py-2 flex items-center justify-between gap-3">
                          <div
                            className={cn(
                              "flex items-center gap-2 text-lg font-mono font-bold transition-colors duration-500",
                              isPaused ? "text-yellow-400" : zoneContent?.textColor ?? "text-white"
                            )}
                          >
                            {!isPaused && (
                              <div className={cn("w-2.5 h-2.5 rounded-full animate-pulse", zoneContent?.dotColor ?? "bg-red-500")} />
                            )}
                            {isPaused && <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
                            {formatTime(recordingTime)}
                            {isPaused && (
                              <span className="text-xs font-semibold text-yellow-400 ms-1">
                                {t("recording_paused", "Paused")}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {talkingPoints && (
                              <span className="text-[10px] text-white/50 hidden sm:block">
                                ↑↓ scroll
                              </span>
                            )}
                            {!isPaused ? (
                              <Button size="sm" variant="outline" onClick={pauseRecording} className="h-7 px-2 text-xs gap-1 bg-white/10 border-white/30 text-white hover:bg-white/20">
                                <Pause className="w-3 h-3" />
                                {t("pause", "Pause")}
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={resumeRecording} className="h-7 px-2 text-xs gap-1 bg-white/10 border-white/30 text-white hover:bg-white/20">
                                <Play className="w-3 h-3" />
                                {t("resume", "Resume")}
                              </Button>
                            )}
                            <Button size="sm" variant="destructive" onClick={stopRecording} className="h-7 px-2 text-xs gap-1">
                              <Square className="w-3 h-3 fill-current" />
                              {t("stop_recording", "Stop")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {type === "audio" && (
                      <div className="flex flex-col items-center justify-center py-6">
                        <div
                          className={cn(
                            "p-8 rounded-full animate-pulse mb-4 transition-colors duration-500",
                            isPaused
                              ? "bg-yellow-100 dark:bg-yellow-950"
                              : currentZone === "green"
                              ? "bg-green-100 dark:bg-green-950"
                              : currentZone === "orange"
                              ? "bg-orange-100 dark:bg-orange-950"
                              : "bg-red-100 dark:bg-red-950"
                          )}
                        >
                          <Mic
                            className={cn(
                              "w-16 h-16 transition-colors duration-500",
                              isPaused
                                ? "text-yellow-600"
                                : currentZone === "green"
                                ? "text-green-600"
                                : currentZone === "orange"
                                ? "text-orange-600"
                                : "text-red-600"
                            )}
                          />
                        </div>
                      </div>
                    )}

                    {/* Timer row for audio */}
                    {type === "audio" && (
                      <div
                        className={cn(
                          "flex items-center justify-center gap-3 text-2xl font-mono font-bold transition-colors duration-500",
                          isPaused ? "text-yellow-600" : zoneContent?.textColor
                        )}
                      >
                        {!isPaused && (
                          <div className={cn("w-3 h-3 rounded-full animate-pulse", zoneContent?.dotColor)} />
                        )}
                        {isPaused && <div className="w-3 h-3 bg-yellow-600 rounded-full" />}
                        {formatTime(recordingTime)}
                      </div>
                    )}

                    {type === "audio" && isPaused && (
                      <p className="text-sm font-semibold text-center text-yellow-600">
                        {t("recording_paused", "Recording Paused")}
                      </p>
                    )}

                    {type === "audio" && !isPaused && zoneContent && (
                      <Alert className={cn("transition-colors duration-500", zoneContent.bgColor, zoneContent.borderColor)}>
                        <AlertDescription className={cn("text-center font-medium", zoneContent.textColor)}>
                          {zoneContent.message}
                        </AlertDescription>
                      </Alert>
                    )}

                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Right side: Talking Points for audio (scrollable teleprompter) */}
                  {talkingPoints && type === "audio" && (
                    <div className="flex-shrink-0 w-72 flex flex-col">
                      <div className="flex flex-col h-full border rounded-lg bg-muted/20">
                        <div className="flex items-center justify-between gap-2 p-3 border-b rounded-t-lg bg-muted/30">
                          <div className="flex items-center gap-2">
                            <ListChecks className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {t("your_talking_points", "Your Talking Points")}
                            </span>
                          </div>
                          <span className="text-[10px] text-muted-foreground">↑↓ scroll</span>
                        </div>
                        <div
                          ref={teleprompterRef}
                          className="flex-1 p-3 overflow-y-auto text-sm prose-sm prose dark:prose-invert max-h-56 scroll-smooth"
                          dangerouslySetInnerHTML={{ __html: talkingPoints }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer buttons — audio only (video has controls overlaid on frame) */}
                {type === "audio" && (
                  <DialogFooter className="gap-2">
                    {!isPaused ? (
                      <Button variant="outline" onClick={pauseRecording} className="gap-2">
                        <Pause className="w-4 h-4" />
                        {t("pause", "Pause")}
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={resumeRecording} className="gap-2">
                        <Play className="w-4 h-4" />
                        {t("resume", "Resume")}
                      </Button>
                    )}
                    <Button variant="destructive" onClick={stopRecording} className="gap-2">
                      <Square className="w-4 h-4 fill-current" />
                      {t("stop_recording", "Stop Recording")}
                      <span className="text-xs opacity-70 ms-1">(Space)</span>
                    </Button>
                  </DialogFooter>
                )}
              </>
            );
          })()}

        {/* PREVIEW STATE (after recording) */}
        {dialogState === "preview" && recordedData && (
          <>
            <DialogHeader>
              <DialogTitle>
                {t("review_your_recording", "Review Your Recording")}
              </DialogTitle>
              <DialogDescription>
                {type === "audio"
                  ? t(
                      "preview_audio_description",
                      "Listen to your recording. You can re-record if needed, or upload to save it."
                    )
                  : t(
                      "preview_video_description",
                      "Watch your recording. You can re-record if needed, or upload to save it."
                    )}
              </DialogDescription>
            </DialogHeader>

            <div className="my-4 space-y-4">
              <div className="p-4 rounded-lg bg-muted/30">
                {type === "audio" ? (
                  <audio
                    ref={previewAudioRef}
                    src={recordedData.blobUrl}
                    controls
                    controlsList="nodownload"
                    className="w-full"
                    onLoadedMetadata={handleMetadataLoaded}
                    preload="metadata"
                  />
                ) : (
                  <div className="w-full aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      ref={previewVideoRef}
                      src={recordedData.blobUrl}
                      controls
                      controlsList="nodownload"
                      className="w-full h-full object-contain rounded-md"
                      onLoadedMetadata={handleMetadataLoaded}
                      preload="metadata"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                <span>
                  {t("duration", "Duration")}:{" "}
                  {formatTime(actualDuration ?? recordedData.duration)}
                </span>
                <span>•</span>
                <span>
                  {t("size", "Size")}: {(recordedData.size / 1024).toFixed(1)}{" "}
                  KB
                </span>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancel}>
                <X className="w-4 h-4 mr-2" />
                {t("cancel", "Cancel")}
              </Button>
              <Button variant="outline" onClick={handleRetake}>
                <RotateCcw className="w-4 h-4 mr-2" />
                {t("retake", "Retake")}
              </Button>
              <Button onClick={handleUpload} disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" />
                {uploading
                  ? t("uploading", "Uploading...")
                  : t("upload", "Upload")}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* PLAYBACK STATE (for existing recordings) */}
        {dialogState === "playback" && recordedData && (
          <div className="relative group">
            {playbackLoading ? (
              <div
                className={cn(
                  "flex items-center justify-center w-full bg-black rounded-lg",
                  type === "video" ? "aspect-video" : "min-h-[80px] py-6"
                )}
              >
                <Loader2 className="w-10 h-10 animate-spin text-white" />
              </div>
            ) : playbackSrc ? (
              type === "audio" ? (
                <audio
                  ref={(el) => {
                    playbackMediaRef.current = el;
                  }}
                  src={playbackSrc}
                  controls
                  controlsList="nodownload"
                  className="w-full"
                  onLoadedMetadata={tryStartPlayback}
                  onDurationChange={tryStartPlayback}
                  preload="metadata"
                />
              ) : (
                <>
                  <div className="w-full aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      ref={(el) => {
                        playbackMediaRef.current = el;
                      }}
                      src={playbackSrc}
                      controls
                      controlsList="nodownload"
                      className="w-full h-full object-contain rounded-md"
                      onLoadedMetadata={tryStartPlayback}
                      onDurationChange={tryStartPlayback}
                      preload="metadata"
                    />
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onOpenChange(false)}
                    className="absolute top-2 right-2 w-7 h-7 p-0 opacity-70 group-hover:opacity-100 transition-opacity"
                    title={t("close", "Close")}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </>
              )
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
